import { useState } from 'react'
import { useQueryParams } from 'utils/hooks/useQueryParams'
import { Box, Divider, Stack } from '@mui/material'
import { theme, Tab, TabGroup } from '@ketch-com/deck'
import { RightsFilters } from '../components/RightsFilters'
import { ChartTrendPeriod } from 'interfaces/rights/rightStats'
import { DataRoleType } from 'interfaces/events'
import { useRightsStatsPeriod } from 'api/rights/queries/useRightsStatsPeriod'
import { getStartAndEndDateFromTrendPeriod } from 'pages/insights/utils'
import dayjs from 'dayjs'
import { StatusBreakdownSection } from './StatusBreakdownSection'
import { ThroughputVelocitySection } from './ThroughputVelocitySection'

export const TrendsTab = () => {
  const { queries, setMultipleQueryParams } = useQueryParams<{
    trendPeriod?: ChartTrendPeriod
    policyScope?: string
    dataSubjectType?: string
    dataRole?: DataRoleType
    rightCode?: string
    applicationCode?: string
    startDate?: string
    endDate?: string
  }>()
  const { trendPeriod = ChartTrendPeriod.DAILY } = queries
  const [currentTab, setCurrentTab] = useState(ChartTrendPeriod.DAILY)

  const defaultEndDate = dayjs().format('YYYY-MM-DD')
  const defaultStartDate = dayjs().subtract(1, 'week').format('YYYY-MM-DD')

  const { data: rightsStatsPeriod, isLoading: isLoadingRightsStatsPeriod } = useRightsStatsPeriod({
    params: {
      policyScope: queries?.policyScope,
      dataSubjectType: queries?.dataSubjectType,
      dataRole: queries?.dataRole,
      rightCode: queries?.rightCode,
      applicationCode: queries?.applicationCode,
      trendPeriod,
      startDate: queries?.startDate ?? defaultStartDate,
      endDate: queries?.endDate ?? defaultEndDate,
    },
  })

  const handleChange = (_: React.SyntheticEvent, newValue: ChartTrendPeriod) => {
    setCurrentTab(newValue)
    const { startDate, endDate } = getStartAndEndDateFromTrendPeriod(newValue)
    setMultipleQueryParams([
      { key: 'startDate', value: startDate },
      { key: 'endDate', value: endDate },
      {
        key: 'trendPeriod',
        value: newValue,
      },
    ])
  }

  return (
    <>
      <RightsFilters />
      <Box
        sx={{
          boxSizing: 'border-box',
          backgroundColor: theme.palette.Common.Container,
          borderRadius: '11px',
          p: 4,
          mt: 3,
        }}
      >
        <Stack spacing={1} pb={1}>
          <Box sx={{ width: '100%' }}>
            <TabGroup value={currentTab} onChange={handleChange}>
              <Tab label="Daily" value={ChartTrendPeriod.DAILY} />
              <Tab label="Weekly" value={ChartTrendPeriod.WEEKLY} />
              <Tab label="Monthly" value={ChartTrendPeriod.MONTHLY_PAST_TWELVE} />
            </TabGroup>
          </Box>
        </Stack>
        <StatusBreakdownSection data={rightsStatsPeriod} isLoading={isLoadingRightsStatsPeriod} />
        <Divider sx={{ my: 2 }} />
        <ThroughputVelocitySection data={rightsStatsPeriod} isLoading={isLoadingRightsStatsPeriod} />
      </Box>
    </>
  )
}
